/**
 * Used to delete a property from the default template
 */
export const MockRemove = Symbol("MockRemove");

// allow removal if the input field type is optional, otherwise only allow replacement
type AllowRemove<Field, Replacement> = undefined extends Field ? Replacement | typeof MockRemove : Replacement;

// We don't use many special types in our code, so adding extra comments here
// prettier-ignore
export type MockPartial<T extends object> = {
  // For each property in the input type T, add an optional property to the output type
  [P in keyof T]?:
  // If the property is an array, allow an arry with the same value type
  T[P] extends (infer V)[] ? AllowRemove<T[P], V[]> :
  // If the property is an object, allow an object with the same fields
  T[P] extends object ? AllowRemove<T[P], MockPartial<T[P]>> :
  // Otherwise it must be a primitive
  AllowRemove<T[P], T[P]>;
}

export type Mocker<T extends object> = (overrides?: MockPartial<T>) => T;

/**
 * Make a copy of a template object with some properties replaced recursively. The overrides can explicitly set values to "null" but "undefined" will be ignored
 *
 * @param template The object to copy
 * @param overrides An object containing fields to replace recursively in the copy
 *
 * @returns A copy of template with fields replaced by values in overrides
 */
export function mockValueWithOverrides<T extends object>(template: T, overrides?: MockPartial<T>): T {
  const copy: T = JSON.parse(JSON.stringify(template));

  function mergeRecursive<U extends object>(destination: U, replacements?: MockPartial<U>) {
    if (replacements !== null) {
      for (const key in replacements) {
        const replacement = replacements[key];
        if (replacement === MockRemove) {
          delete destination[key];
        } else if (replacement !== undefined) {
          const destinationValue = destination[key];
          if (
            destinationValue !== null &&
            typeof destinationValue === "object" &&
            !Array.isArray(destinationValue) &&
            replacement !== null &&
            typeof replacement === "object"
          ) {
            mergeRecursive(destinationValue, replacement);
          } else {
            Object.assign(destination, { [key]: replacement });
          }
        }
      }
    }
  }

  mergeRecursive(copy, overrides);

  return copy;
}

/**
 * Make a function that returns mock values and supports adding recursive overrides
 *
 * @see {@link mockValueWithOverrides}
 *
 * @param template default value to return
 * @returns A function which provides mock values
 */
export function makeMocker<T extends object>(template: T): Mocker<T> {
  return (overrides?: MockPartial<T>) => mockValueWithOverrides(template, overrides);
}
